import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Buton, Etiket, Girdi, toast } from '@emanetx/ui';
import type { Ortak } from '@emanetx/sdk';
import { useOrtakGuncelle, useOrtakOlustur } from './api.js';

interface Ozellikler {
  acik: boolean;
  onDegisim: (a: boolean) => void;
  ortak?: Ortak | null;
}

export function OrtakDialog({ acik, onDegisim, ortak }: Ozellikler) {
  const duzenleme = !!ortak;
  const olustur = useOrtakOlustur();
  const guncelle = useOrtakGuncelle();

  const [ad, setAd] = useState('');
  const [eposta, setEposta] = useState('');
  const [telefon, setTelefon] = useState('');
  const [iletisimAdSoyad, setIletisimAdSoyad] = useState('');
  const [komisyonYuzde, setKomisyonYuzde] = useState('0');
  const [bankaIban, setBankaIban] = useState('');
  const [bankaAd, setBankaAd] = useState('');
  const [vergiNo, setVergiNo] = useState('');
  const [aktif, setAktif] = useState(true);

  useEffect(() => {
    if (!acik) return;
    if (ortak) {
      setAd(ortak.ad);
      setEposta(ortak.eposta);
      setTelefon(ortak.telefon ?? '');
      setIletisimAdSoyad(ortak.iletisimAdSoyad ?? '');
      setKomisyonYuzde(ortak.komisyonYuzde);
      setBankaIban(ortak.bankaIban ?? '');
      setBankaAd(ortak.bankaAd ?? '');
      setVergiNo(ortak.vergiNo ?? '');
      setAktif(ortak.aktif);
    } else {
      setAd('');
      setEposta('');
      setTelefon('');
      setIletisimAdSoyad('');
      setKomisyonYuzde('0');
      setBankaIban('');
      setBankaAd('');
      setVergiNo('');
      setAktif(true);
    }
  }, [acik, ortak]);

  const calisiyor = olustur.isPending || guncelle.isPending;
  const formGecerli =
    ad.trim().length >= 2 && /^.+@.+\..+$/.test(eposta) && /^\d+(\.\d{1,2})?$/.test(komisyonYuzde);

  const gonder = async () => {
    const govde = {
      ad: ad.trim(),
      eposta: eposta.toLowerCase().trim(),
      telefon: telefon || undefined,
      iletisimAdSoyad: iletisimAdSoyad || undefined,
      komisyonYuzde,
      bankaIban: bankaIban || undefined,
      bankaAd: bankaAd || undefined,
      vergiNo: vergiNo || undefined,
      aktif,
    };
    try {
      if (duzenleme && ortak) {
        await guncelle.mutateAsync({ id: ortak.id, ...govde });
        toast.success('Ortak güncellendi');
      } else {
        await olustur.mutateAsync(govde);
        toast.success('Ortak oluşturuldu');
      }
      onDegisim(false);
    } catch (err) {
      const m =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'İşlem başarısız';
      toast.error(m);
    }
  };

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[95vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold">
              {duzenleme ? 'Ortağı Düzenle' : 'Yeni Ortak'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Kapat" className="text-fume-400 hover:text-fume-700">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (formGecerli && !calisiyor) void gonder();
            }}
            className="space-y-4"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Etiket htmlFor="ad">
                  Şirket Adı <span className="text-red-500">*</span>
                </Etiket>
                <Girdi id="ad" value={ad} onChange={(e) => setAd(e.target.value)} required />
              </div>
              <div>
                <Etiket htmlFor="ep">
                  E-posta <span className="text-red-500">*</span>
                </Etiket>
                <Girdi
                  id="ep"
                  type="email"
                  value={eposta}
                  onChange={(e) => setEposta(e.target.value)}
                  required
                />
              </div>
              <div>
                <Etiket htmlFor="il">İletişim Adı</Etiket>
                <Girdi
                  id="il"
                  value={iletisimAdSoyad}
                  onChange={(e) => setIletisimAdSoyad(e.target.value)}
                />
              </div>
              <div>
                <Etiket htmlFor="tel">Telefon</Etiket>
                <Girdi id="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)} />
              </div>
              <div>
                <Etiket htmlFor="ky">Komisyon % (varsayılan)</Etiket>
                <Girdi
                  id="ky"
                  value={komisyonYuzde}
                  onChange={(e) => setKomisyonYuzde(e.target.value)}
                  placeholder="20"
                />
              </div>
              <div>
                <Etiket htmlFor="vn">Vergi No</Etiket>
                <Girdi id="vn" value={vergiNo} onChange={(e) => setVergiNo(e.target.value)} />
              </div>
              <div>
                <Etiket htmlFor="bi">IBAN</Etiket>
                <Girdi
                  id="bi"
                  value={bankaIban}
                  onChange={(e) => setBankaIban(e.target.value)}
                  placeholder="TR..."
                />
              </div>
              <div>
                <Etiket htmlFor="ba">Banka Adı</Etiket>
                <Girdi id="ba" value={bankaAd} onChange={(e) => setBankaAd(e.target.value)} />
              </div>
            </div>

            <label className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 flex h-11 cursor-pointer items-center gap-2 rounded-lg border bg-white px-3 text-sm">
              <input type="checkbox" checked={aktif} onChange={(e) => setAktif(e.target.checked)} />
              Aktif
            </label>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Buton stil="cizgili" boyut="orta" disabled={calisiyor}>
                  İptal
                </Buton>
              </Dialog.Close>
              <Buton type="submit" disabled={!formGecerli || calisiyor}>
                {calisiyor ? 'Kaydediliyor...' : duzenleme ? 'Güncelle' : 'Oluştur'}
              </Buton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
